import { useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { pageTitle, pageTitleKey } from '@/config/pageTitles';

/**
 * Keeps `document.title` in step with the current route (HS-1221 follow-up).
 *
 * Deliberately a hook over the router's own `head` option: `head` is evaluated when a route
 * matches, so a title taken from `t()` there would keep the language it was matched in until the
 * next navigation — and admin has a language picker on `/settings`, where the stale title would
 * be the first thing a user sees after switching. Reading `t` inside an effect re-runs on the
 * language change itself.
 *
 * No `react-helmet` (or any head-manager dependency): one string on one element does not need a
 * library, and nothing else in the document head varies by route.
 */
export function useDocumentTitle(): void {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    const key = pageTitleKey(pathname);
    document.title = pageTitle(key ? t(key) : null);
  }, [pathname, t]);
}
